import { useCallback, useEffect, useRef } from 'react'
import {
  getActiveContext,
  subscribeActiveContext,
  type ActiveContextListener,
} from '@kiao-voice/api-client'

export interface WorkspaceScopedLoadContext {
  /** Workspace key that caused this load (`personal` or `org:<id>`). */
  workspaceKey: string
  /** False once a later workspace load supersedes this one or the hook unmounts. */
  isCurrent: () => boolean
}

export interface UseWorkspaceScopedLoaderOptions {
  enabled?: boolean
  /** Clear every workspace-owned value synchronously before a switched load. */
  clear: () => void
  /** Load data for the current workspace. Call `isCurrent()` before committing. */
  load: (context: WorkspaceScopedLoadContext) => Promise<void> | void
  /** Test seams; production callers use the shared active-context store. */
  readWorkspace?: () => string
  subscribe?: (listener: ActiveContextListener) => () => void
}

export interface WorkspaceScopedLoaderControls {
  reload: () => Promise<void>
  /** Capture the current workspace generation before an async mutation. */
  captureWorkspace: () => () => boolean
}

/**
 * Runs a loader on mount and again whenever the active workspace changes.
 *
 * A user-driven switch is a hard data boundary: `clear` runs in the same
 * notification turn, before the next request starts. Each run also gets an
 * `isCurrent` guard so an older response cannot repopulate state after the
 * workspace has changed.
 */
export function useWorkspaceScopedLoader({
  enabled = true,
  clear,
  load,
  readWorkspace = getActiveContext,
  subscribe = subscribeActiveContext,
}: UseWorkspaceScopedLoaderOptions): WorkspaceScopedLoaderControls {
  const generationRef = useRef(0)
  const loadRef = useRef(load)
  const clearRef = useRef(clear)
  const readWorkspaceRef = useRef(readWorkspace)
  loadRef.current = load
  clearRef.current = clear
  readWorkspaceRef.current = readWorkspace

  const run = useCallback(async (workspaceKey?: string) => {
    const generation = ++generationRef.current
    await loadRef.current({
      workspaceKey: workspaceKey ?? readWorkspaceRef.current(),
      isCurrent: () => generationRef.current === generation,
    })
  }, [])

  useEffect(() => {
    if (!enabled) {
      generationRef.current += 1
      return
    }

    void run()
    const unsubscribe = subscribe((workspaceKey) => {
      generationRef.current += 1
      clearRef.current()
      void run(workspaceKey)
    })

    return () => {
      generationRef.current += 1
      unsubscribe()
    }
  }, [enabled, run, subscribe])

  return {
    reload: useCallback(() => run(), [run]),
    captureWorkspace: useCallback(() => {
      const generation = generationRef.current
      return () => generationRef.current === generation
    }, []),
  }
}
